import { React, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { FacultyEdit } from "..";
import { Snackbar, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReactPaginate from "react-paginate";
import "../../styles/Overflow.css";

function FacultyTable(props) {
  const { faculties, updateTable, setUpdateTable } = props;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleDelete = (facultyId) => {
    const body = new FormData();
    body.append("facultyId", facultyId);
    axios.post("/faculty/confirmDeleteFaculty", body).then((res) => {
      const data = res.data;
      if (data.success) {
        setUpdateTable(!updateTable);
      }
      else {
        if (data.inactive === "true") {
          confirmDelete(facultyId, data);
          return;
        }
        setSnackbarMessage(data.message);
        setSnackbarOpen(true);
        return;
      }
    });
  };

  const confirmDelete = (facultyId, data) => {
    if (confirm(data.message + " Are you sure you would like to delete this faculty member?")) {
      const body = new FormData();
      body.append("facultyId", facultyId);
      axios.post("/faculty/deleteFaculty", body).then((res) => {
        const data = res.data;
        if (data.success) {
          setUpdateTable(!updateTable);
        }
        else {
          setSnackbarMessage(data.message);
          setSnackbarOpen(true);
          return;
        }
      });
    }
    return;
  };

  // Here we use item offsets; we could also use page offsets
  // following the API or data you're working with.
  const [itemOffset, setItemOffset] = useState(0);

  const items = faculties.map((faculty, index) => (
    <div key={index}>
      <div className="FacultyRow-container flex rounded-md border-2 border-brand-tertiary drop-shadow-2xl mb-2">
        <div className="FacultyRow-firstName text-xl flex justify-center font-brand-primary w-3/12 border-r-2 py-2">
          <p className="overFlow">{faculty.firstName}</p>
        </div>
        <div className="FacultyRow-lastName text-xl flex justify-center font-brand-primary w-3/12 border-r-2 py-2">
          <p className="overFlow">{faculty.lastName}</p>
        </div>
        <div className="FacultyRow-pid text-xl flex justify-center font-brand-primary w-4/12 border-r-2 py-2">
          <p className="overFlow">{faculty.pid}</p>
        </div>
        <div className="FacultyRow-email text-xl flex justify-center font-brand-primary w-4/12 border-r-2 py-2">
          <p className="overFlow">{faculty.email}</p>
        </div>
        <div className="FacultyRow-websiteURL text-xl flex justify-center font-brand-primary w-4/12 border-r-2 py-2">
          <p className="overFlowBig">{faculty.websiteURL}</p>
        </div>
        <div className="FacultyRow-editDelete text-xl flex justify-center w-2/12 py-2">
          <FacultyEdit
            updateTable={updateTable}
            setUpdateTable={setUpdateTable}
            faculty={faculty}
          />
          <button
            className="FacultyRow-deleteButton flex"
            onClick={() => handleDelete(faculty.facultyId)}
          >
            <CloseIcon fontSize="large" className="fill-brand-primary" />
          </button>
        </div>
      </div>
    </div>
  ));

  // Simulate fetching items from another resources.
  // (This could be items from props; or items loaded in a local state
  // from an API endpoint with useEffect and useState)
  const itemsPerPage = 5;
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = items.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(items.length / itemsPerPage);

  // Invoke when user click to request another page.
  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % items.length;
    setItemOffset(newOffset);
  };

  if (faculties.length === 0) {
    return <div className="FacultyTable bg-white p-2 rounded-xl w-full">No faculty found.</div>;
  } else {
    return (
      <div className="FacultyTable bg-white p-2 rounded-xl w-full">
        <ReactPaginate
          breakLabel="..."
          nextLabel=">"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="<"
          renderOnZeroPageCount={null}
          className="flex w-full justify-between mb-6 mt-4 [&>li>a]:py-4 [&>li>a]:px-6 [&>li>a]:border [&>li>a]:rounded-lg [&>li.selected>a]:border-brand-primary"
        />
        <div className="FacultyTable-container flex rounded-md border-2 border-black drop-shadow-2xl mb-1">
          <div className="FacultyTable-firstName text-xl flex justify-center font-brand-primary w-3/12 border-black border-r-2 py-2">
            First Name
          </div>
          <div className="FacultyTable-lastName text-xl flex justify-center font-brand-primary w-3/12 border-black border-r-2 py-2">
            Last Name
          </div>
          <div className="FacultyTable-pid text-xl flex justify-center font-brand-primary w-4/12 border-black border-r-2 py-2">
            Pid
          </div>
          <div className="FacultyTable-email text-xl flex justify-center font-brand-primary w-4/12 border-black border-r-2 py-2">
            Email
          </div>
          <div className="FacultyTable-websiteURL text-xl flex justify-center font-brand-primary w-4/12 border-black border-r-2 py-2">
            Website URL
          </div>
          <div className="FacultyTable-editDelete text-xl flex justify-center w-2/12 py-2">
            <div className="flex mr-4">
              Edit
            </div>
            /
            <div className="flex ml-4">
              Delete
            </div>
          </div>
        </div>
        {currentItems}
        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
          onClose={() => {
            setSnackbarOpen(false);
            setSnackbarMessage("");
          }
          }
        >
          <Alert
            onClose={() => {
              setSnackbarOpen(false);
              setSnackbarMessage("");
            }
            }
            severity={snackbarMessage == "User profile updated!" ? "success" : "error"}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

FacultyTable.propTypes = {
  faculties: PropTypes.arrayOf(PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    facultyId: PropTypes.number,
    pid: PropTypes.string,
    websiteURL: PropTypes.string,
  })),
  updateTable: PropTypes.bool,
  setUpdateTable: PropTypes.func,
};

FacultyTable.defaultProps = {
  faculties: [],
  updateTable: false,
  setUpdateTable: () => { },
};

export default FacultyTable;
